/**
 * CustomersPanel — tenant detail Customers tab (TMMPDM-0004). Read-only list of
 * the tenant's end users from `listMembers` (`GET …/users`, GUEST filtered by
 * the API module). Server-paged with a debounced search. Invite customer is
 * tmm-api `POST …/invitations` (TMMPDM-0017: every tenant is complete, so always offered).
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { listMembers } from '@/api/saas/tenants';
import type { PagedResponse } from '@/api/types';
import type { TenantMemberDto } from '@/api/saas/types';
import type { AdminInvitationResponse } from '@/api/tmm/types';
import { InviteCustomerDialog } from './InviteCustomerDialog';
import { InvitationsPanel } from './InvitationsPanel';
import {
  PeopleLeafLayout,
  PeopleTableContainer,
  PeopleToolbar,
} from '../partners/peopleLeafLayout';
import { StatusChip } from '@/status';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;

interface CustomersPanelProps {
  tenantId: number;
  slug: string;
}

export function CustomersPanel({ tenantId, slug }: CustomersPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);
  // Invited but not emailed is a warning banner; a sent invitation is a quiet toast (TMMPDM-0034).
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const requestKey = `${tenantId}:${page}:${query}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: PagedResponse<TenantMemberDto> | null;
    loadFailed: boolean;
    failure: unknown;
  }>({ requestKey: null, result: null, loadFailed: false, failure: null });

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    listMembers(tenantId, { page, size: PAGE_SIZE, ...(query ? { q: query } : {}) })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, loadFailed: false, failure: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResponse({ requestKey, result: null, loadFailed: true, failure: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, page, query, reloadToken, requestKey]);

  const current = response.requestKey === requestKey;
  const result = current ? response.result : null;
  const loadFailed = current ? response.loadFailed : false;
  const loading = !current;
  const rows = result?.content ?? [];
  const totalPages = result?.totalPages ?? 0;
  const pageNumber = (result?.page ?? page) + 1;

  const onInvited = (result: AdminInvitationResponse) => {
    setInviteOpen(false);
    setInvitationsRefreshKey((key) => key + 1);
    if (result.emailSent === true) {
      setInviteNote(null);
      notify.success(
        t('customers.invite.sent', 'Invitation sent to {{email}}.', { email: result.email }),
      );
    } else {
      setInviteNote(
        t(
          'customers.invite.created',
          'Invitation created for {{email}}; the email could not be sent.',
          { email: result.email },
        ),
      );
    }
  };

  return (
    <PeopleLeafLayout
      caption={
        <>
          <Typography variant="h2" component="h2" gutterBottom>
            {t('tenantDetail.customers.title', 'Customers')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'tenantDetail.customers.help',
              'End users of this tenant. Subscription details come from the product backend in a later release.',
            )}
          </Typography>
        </>
      }
      invitations={
        <InvitationsPanel
          tenantId={tenantId}
          refreshKey={invitationsRefreshKey}
          audience="customer"
        />
      }
    >
      {inviteNote ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setInviteNote(null)}>
          {inviteNote}
        </Alert>
      ) : null}

      <PeopleToolbar>
        <TextField
          size="small"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setPage(0);
          }}
          label={t('tenantDetail.customers.searchLabel', 'Search users')}
          sx={{ minWidth: 260 }}
        />
        <Button variant="contained" onClick={() => setInviteOpen(true)}>
          {t('customers.invite.action', 'Invite customer…')}
        </Button>
      </PeopleToolbar>

      {loadFailed ? (
        <ErrorState
          error={response.failure}
          message={t('tenantDetail.customers.loadError', 'Could not load customers.')}
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      ) : (
        <PeopleTableContainer busy={loading}>
          <Table
            size="small"
            aria-label={t('tenantDetail.customers.tableAriaLabel', 'Tenant customers')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('tenantDetail.customers.col.email', 'Email')}</TableCell>
                <TableCell>{t('tenantDetail.customers.col.name', 'Name')}</TableCell>
                <TableCell>{t('tenantDetail.customers.col.status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common.loading', 'Loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    {query ? (
                      <EmptyState
                        title={t(
                          'tenantDetail.customers.emptySearch',
                          'No customers match your search.',
                        )}
                        filtered={{
                          onClear: () => {
                            setSearchInput('');
                            setQuery('');
                            setPage(0);
                          },
                        }}
                      />
                    ) : (
                      <EmptyState title={t('tenantDetail.customers.empty', 'No customers yet.')} />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((member) => (
                  <TableRow key={member.membershipId} hover>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.name ?? '—'}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={member.status}
                        label={t(`tenantDetail.members.status.${member.status}`, member.status)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </PeopleTableContainer>
      )}

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
      >
        <Button
          variant="outlined"
          disabled={loading || (result?.page ?? page) <= 0}
          onClick={() => setPage((current) => Math.max(0, current - 1))}
        >
          {t('tenants.pagination.previous', 'Previous')}
        </Button>
        <Typography variant="body2" aria-live="polite">
          {t('tenantDetail.customers.pagination.pageOf', 'Page {{page}} of {{total}}', {
            page: totalPages === 0 ? 0 : pageNumber,
            total: totalPages,
          })}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading || totalPages === 0 || (result?.page ?? page) + 1 >= totalPages}
          onClick={() => setPage((current) => current + 1)}
        >
          {t('tenants.pagination.next', 'Next')}
        </Button>
      </Stack>

      <InviteCustomerDialog
        open={inviteOpen}
        slug={slug}
        onClose={() => setInviteOpen(false)}
        onDone={onInvited}
      />
    </PeopleLeafLayout>
  );
}
